import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Baby, Lock, Mail } from "lucide-react";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function loginWithGoogle() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function LoginPage() {
  const { login, mfaChallenge, status, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mfa, setMfa] = useState(null);
  const [mfaCode, setMfaCode] = useState("");

  if (status === "authed") {
    if (user?.role === "ouder") return <Navigate to="/portaal" replace />;
    if (user?.role === "groep_account") return <Navigate to="/app/groep-dashboard" replace />;
    if (user?.role === "pedagogisch_medewerker") return <Navigate to="/app/medewerker-dashboard" replace />;
    if (user?.role === "pedagogisch") return <Navigate to="/app/planning" replace />;
    if (user?.role === "directeur") return <Navigate to="/app/directie" replace />;
    return <Navigate to="/app" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welkom terug");
    } else if (res.mfa_required) {
      setMfa({ user_id: res.user_id, email: res.email });
    } else {
      setError(res.error);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    if (!mfaCode || mfaCode.length < 6) return setError("Voer 6-cijferige code in");
    setLoading(true); setError(null);
    const res = await mfaChallenge(mfa.user_id, mfaCode);
    setLoading(false);
    if (res.ok) toast.success("Welkom terug");
    else setError(res.error);
  };

  const setDemo = (u, p) => { setEmail(u); setPassword(p); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[color:var(--bone)] subtle-grain">
      {/* Left visual */}
      <div className="hidden lg:flex relative flex-col justify-between p-12 text-white" style={{ background: "linear-gradient(180deg, #2D4A3E 0%, #1F3529 100%)" }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center">
            <Baby className="h-5 w-5" strokeWidth={2.2} />
          </div>
          <div className="font-display font-bold text-xl">Webster<span className="text-[color:var(--terracotta)]">.</span></div>
        </div>
        <div className="max-w-md">
          <div className="text-[11px] uppercase tracking-widest text-white/60 mb-4">Kinderopvang OS</div>
          <h2 className="font-display text-4xl leading-tight tracking-tight mb-4">
            Rustig en overzichtelijk beheer, precies voor de Nederlandse kinderopvang.
          </h2>
          <p className="text-white/70 text-sm leading-relaxed">
            Contracten, planning, presentie, facturatie en compliance-ondersteuning (LRK, KOT, BKR-controle, AVG) — modern, veilig en met volledige audit trail. Voor definitieve BKR-berekening: raadpleeg de <a href="https://www.1ratio.nl" target="_blank" rel="noopener noreferrer" className="underline">officiële SZW-rekentool</a>.
          </p>
          <div className="mt-6 text-[11px] text-white/40">
            Powered by <span className="text-white/80 font-semibold">Webster</span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-xs text-white/60">
          <div><div className="text-white font-semibold text-2xl">3</div>Wet Kinderopvang</div>
          <div><div className="text-white font-semibold text-2xl">100%</div>Audit trail</div>
          <div><div className="text-white font-semibold text-2xl">EU</div>AVG-compliant</div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={mfa ? submitMfa : submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div>
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "var(--sage)" }}>
                <Baby className="h-5 w-5 text-white" />
              </div>
              <div className="font-display font-bold text-xl">Webster<span className="text-[color:var(--terracotta)]">.</span></div>
            </div>
            <h1 className="font-display text-3xl tracking-tight">Inloggen</h1>
            <p className="text-neutral-500 text-sm mt-1">Welkom terug — meld je aan om verder te gaan.</p>
          </div>

          <div className="space-y-4">
            {mfa ? (
              <div className="space-y-4" data-testid="mfa-challenge">
                <div className="p-3 rounded border border-emerald-200 bg-emerald-50 text-sm text-emerald-900">
                  🔐 <strong>Tweestapsverificatie vereist</strong><br/>
                  <span className="text-xs">Open je authenticator-app en voer de 6-cijferige code in.</span>
                </div>
                <div>
                  <Label htmlFor="mfa-code">Verificatiecode</Label>
                  <Input id="mfa-code" data-testid="mfa-code" type="text" inputMode="numeric" maxLength={8}
                    value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/[^0-9A-Z]/g, ""))}
                    placeholder="123456" autoFocus className="text-center font-mono text-lg tracking-widest mt-1" />
                  <p className="text-[11px] text-neutral-500 mt-1">
                    Geen toegang tot je authenticator? Gebruik één van je recovery-codes.
                  </p>
                </div>
                <button type="button" onClick={() => { setMfa(null); setMfaCode(""); setError(null); }}
                  className="text-xs text-neutral-500 underline">← Terug naar login</button>
              </div>
            ) : (
              <>
                <div>
                  <Label htmlFor="email">E-mail of gebruikersnaam</Label>
                  <div className="relative mt-1">
                    <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input id="email" data-testid="login-email" type="text" value={email} required
                      onChange={(e) => setEmail(e.target.value)} className="pl-9" placeholder="jij@voorbeeld.nl of groepslogin" />
                  </div>
                  <p className="text-[11px] text-neutral-500 mt-1">
                    Persoonlijk account: e-mail · Groepslogin: gebruikersnaam
                  </p>
                </div>
                <div>
                  <Label htmlFor="password">Wachtwoord</Label>
                  <div className="relative mt-1">
                    <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input id="password" data-testid="login-password" type="password" value={password} required
                      onChange={(e) => setPassword(e.target.value)} className="pl-9" placeholder="••••••••" />
                  </div>
                  <div className="text-right mt-1">
                    <a href="/forgot-password" className="text-xs text-[color:var(--sage)] hover:underline" data-testid="login-forgot-link">
                      Wachtwoord vergeten?
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}

          <Button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full h-11 text-white transition-colors"
            style={{ background: "var(--sage)" }}
            onMouseEnter={(e) => e.currentTarget.style.background = "var(--sage-hover)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "var(--sage)"}>
            {loading ? "Bezig…" : "Inloggen"}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-neutral-200" /></div>
            <div className="relative flex justify-center text-[11px] uppercase tracking-widest">
              <span className="bg-[color:var(--bone)] px-3 text-neutral-400">of</span>
            </div>
          </div>

          <Button
            type="button"
            data-testid="login-google"
            onClick={loginWithGoogle}
            variant="outline"
            className="w-full h-11 border-neutral-300 hover:bg-neutral-50 gap-3 font-medium"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Inloggen met Google
          </Button>

          {(() => {
            const showByEnv = process.env.REACT_APP_SHOW_DEMO_ACCOUNTS === "true";
            // Vangnet: verberg tiles ALTIJD buiten de preview-omgeving,
            // ook als de env-var per ongeluk is gezet in productie.
            const isPreviewHost = typeof window !== "undefined"
              && (/\.preview\.emergentagent\.com$/i.test(window.location.hostname)
                  || window.location.hostname === "localhost");
            return showByEnv && isPreviewHost;
          })() && (
          <div className="pt-6 border-t border-neutral-200">
            <div className="text-xs uppercase tracking-widest text-neutral-400 mb-3">
              🎭 Regenboog-demo · Kies rol om direct in te loggen
            </div>
            <div className="grid grid-cols-1 gap-2">
              {[
                { l: "Platform-superadmin", e: "gaddurkhalid@gmail.com", p: "Kovnet2026!" },
                { l: "Directeur KDV", e: "directeur@regenboog-demo.example", p: "Demo2026!" },
                { l: "Locatiemanager Oost", e: "manager.oost@regenboog-demo.example", p: "Demo2026!" },
                { l: "Locatiemanager Zuid", e: "manager.zuid@regenboog-demo.example", p: "Demo2026!" },
                { l: "Financieel medewerker", e: "financieel@regenboog-demo.example", p: "Demo2026!" },
                { l: "Pedagogisch medewerker", e: "pedagogisch@regenboog-demo.example", p: "Demo2026!" },
                { l: "Groepslogin — Raketjes", e: "raketjes-west", p: "Groep2026!" },
                { l: "Ouder (portaal)", e: "ouder@regenboog-demo.example", p: "Demo2026!" },
              ].map(d => (
                <button
                  key={d.e}
                  type="button"
                  data-testid={`demo-${d.l.toLowerCase().replace(/\s+/g, '-').replace(/[()/]/g, '').replace(/--+/g, '-')}`}
                  onClick={() => setDemo(d.e, d.p)}
                  className="text-left flex items-center justify-between px-3 py-2 rounded-md border border-neutral-200 hover:border-[color:var(--sage)] hover:bg-[color:var(--sage-100)]/50 transition-colors"
                >
                  <span className="text-sm font-medium text-neutral-800">{d.l}</span>
                  <span className="text-xs text-neutral-500 font-mono truncate ml-2">{d.e}</span>
                </button>
              ))}
            </div>
          </div>
          )}
        </form>
      </div>
    </div>
  );
}
